import { useEffect, useMemo, useRef, useState } from 'react'
import { getLoanPayees } from 'services'
import { Input2 } from 'stories/components'

/** Someone on the loan who can be paid. Shape returned by GET /loan/payables/payees. */
export interface Payee {
  payeeType: string
  payeeRefId: number | null
  name: string
  address: string
  achReady: boolean
  achBlockedReason: string
}

/** Characters typed before the vendor book is worth asking about. */
const MIN_SEARCH_LENGTH = 2

/** How long a pause in typing counts as "done typing" before the server is asked. */
const SEARCH_DEBOUNCE_MS = 350

/**
 * Typeahead for choosing who is being paid: type freely, and the loan's payees
 * (borrower, participants, contractors) surface as suggestions. Picking one
 * links its payeeType / payeeRefId / address; free text stays an "Other" payee.
 *
 * `payees` is the loan's own parties, which are a handful. The accounting vendor book is thousands
 * deep and is not in that list — typing searches it on the server instead, so every screen holding
 * this picker no longer downloads the whole book before anyone has typed anything.
 */
export const PayeeCombo = ({
  payees,
  value,
  loanNumber,
  onType,
  onPick,
  onBlur,
  disabled = false,
  title = 'Who is being paid',
}: {
  payees: Payee[]
  value: string
  /** Only needed where the loan-number header isn't set for the caller (e.g. a shared view). */
  loanNumber?: number | string
  onType: (name: string) => void
  onPick: (payee: Payee) => void
  onBlur?: () => void
  disabled?: boolean
  title?: string
}) => {
  const keyOf = (p: Payee) => `${p.payeeType}:${p.payeeRefId ?? ''}`
  const containerRef = useRef<HTMLDivElement>(null)
  const [open, setOpen] = useState(false)
  const [active, setActive] = useState(0)
  const [remote, setRemote] = useState<Payee[]>([])
  const [searching, setSearching] = useState(false)

  // Search the vendor book for what is being typed. Gated on the list being open and on a couple of
  // characters, so opening the field costs nothing and a single letter doesn't ask for half the book.
  useEffect(() => {
    const query = value.trim()
    if (!open || query.length < MIN_SEARCH_LENGTH) {
      setRemote([])
      setSearching(false)
      return
    }
    let cancelled = false
    setSearching(true)
    const timer = setTimeout(() => {
      getLoanPayees(loanNumber, query)
        .then((res: any) => {
          if (!cancelled) setRemote(Array.isArray(res?.payees) ? res.payees : [])
        })
        .catch(() => {
          if (!cancelled) setRemote([])
        })
        .finally(() => {
          if (!cancelled) setSearching(false)
        })
    }, SEARCH_DEBOUNCE_MS)
    return () => {
      cancelled = true
      clearTimeout(timer)
    }
  }, [value, open, loanNumber])

  // The loan's parties filtered by the typed text, then whatever the search turned up that they do
  // not already cover. Parties stay first: they are the ones this loan is most likely paying.
  const matches = useMemo(() => {
    const q = value.trim().toLowerCase()
    const local = q ? payees.filter((p) => `${p.name} ${p.payeeType}`.toLowerCase().includes(q)) : payees
    const seen = new Set(local.map(keyOf))
    return [...local, ...remote.filter((p) => !seen.has(keyOf(p)))].slice(0, 12)
  }, [payees, value, remote])

  useEffect(() => setActive(0), [value])

  useEffect(() => {
    const onClickOutside = (e: MouseEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onClickOutside)
    return () => document.removeEventListener('mousedown', onClickOutside)
  }, [])

  const choose = (p: Payee) => {
    onPick(p)
    setOpen(false)
  }

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (!open || !matches.length) return
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setActive((i) => Math.min(i + 1, matches.length - 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setActive((i) => Math.max(i - 1, 0))
    } else if (e.key === 'Enter') {
      if (matches[active]) {
        e.preventDefault()
        choose(matches[active])
      }
    } else if (e.key === 'Escape') {
      setOpen(false)
    }
  }

  return (
    <div className="relative" ref={containerRef}>
      <Input2
        title={title}
        value={value}
        disabled={disabled}
        onChange={(v: string) => {
          onType(v)
          setOpen(true)
        }}
        onFocus={() => !disabled && setOpen(true)}
        onKeyDown={onKeyDown}
        onBlur={onBlur}
      />
      {open && (matches.length > 0 || searching) && (
        <div className="absolute left-0 right-0 z-[70] -mt-2 max-h-56 overflow-auto rounded-lg border border-slate-200 bg-white shadow-lg">
          {searching && matches.length === 0 && <div className="px-3 py-1.5 text-sm text-gray-500">Searching…</div>}
          {matches.map((p, i) => (
            <button
              type="button"
              key={keyOf(p)}
              // onMouseDown fires before the input blur, so the pick lands before the list closes.
              onMouseDown={(e) => {
                e.preventDefault()
                choose(p)
              }}
              onMouseEnter={() => setActive(i)}
              className={`flex w-full items-center justify-between gap-2 px-3 py-1.5 text-left text-sm ${
                i === active ? 'bg-shade-blue/10' : ''
              }`}
            >
              <span>
                <span className="font-medium">{p.name}</span>
                <span className="text-gray-500"> ({p.payeeType})</span>
              </span>
              {!p.achReady && <span className="whitespace-nowrap text-xs text-amber-600">check only</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
